/*
  NOTE: In the course platform, SpringVisualizerDemo is a Demo component with optional sliders for stiffness/damping/mass. For the linear() timing function blog post, we’re only using this in one place, to quickly show what we’re trying to model, so I removed all this extra stuff, and updated the layout to match LinearDemo.

  If I ever want to include this full demo component with sliders, I should copy the course platform version over again, from scratch.
*/
import * as React from 'react';
import { styled } from '@linaria/react';
import { Play } from 'lucide-react';

import ButtonPrimary from '@/components/ButtonPlayful/ButtonPrimary';
import ColorModeContainer from '@/components/ColorModeContainer';

import { CONTAINER_WIDTH } from '../AnimatedBlock';
import SpringGraph from './SpringGraph';
import SpringAnimation from './SpringAnimation';

interface Props {
  stiffness?: number;
  damping?: number;
  mass?: number;
}

function SpringVisualizerDemo({
  stiffness = 200,
  damping = 20,
  mass = 1,
}: Props) {
  const [triggeredAt, setTriggeredAt] = React.useState<number | null>(
    null
  );
  const [direction, setDirection] = React.useState<'left' | 'right'>(
    'left'
  );

  return (
    <OuterWrapper colorMode="dark">
      <GraphWrapper>
        <SpringGraph
          stiffness={stiffness}
          damping={damping}
          mass={mass}
        />
      </GraphWrapper>
      <BlockWrapper>
        <SpringAnimation
          stiffness={stiffness}
          damping={damping}
          mass={mass}
          triggeredAt={triggeredAt}
          direction={direction}
        />
      </BlockWrapper>
      <HorizontalRule />

      <ActionButton
        Icon={Play}
        onClick={() => {
          setTriggeredAt(performance.now());
          setDirection(direction === 'left' ? 'right' : 'left');
        }}
      >
        Run Animation
      </ActionButton>
    </OuterWrapper>
  );
}

const OuterWrapper = styled(ColorModeContainer)`
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 16px auto;
  padding: 1rem;
  border-radius: 8px;
  border: var(--page-border);
  isolation: isolate;

  @media (max-width: 25rem) {
    width: calc(100% + var(--viewport-padding) * 2);
    max-width: revert;
    margin-inline: calc(var(--viewport-padding) * -1);
    overflow: hidden;
    margin-bottom: 48px;
  }
`;

const GraphWrapper = styled.div`
  padding-inline: 1.25rem;
`;

const BlockWrapper = styled.div`
  margin-top: 36px;
`;
const HorizontalRule = styled.div`
  width: calc(100% + 2rem);
  margin: 36px -1rem 1rem;
  height: 1px;
  background: var(--color-gray-200);
`;

const ActionButton = styled(ButtonPrimary)`
  width: ${CONTAINER_WIDTH}px;
  max-width: 100%;
`;

export default SpringVisualizerDemo;
